import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, Clock3, Laptop, LogOut, RefreshCw, ShieldCheck, Wifi } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useAuth } from "../context/AuthContext";

const OWNER_EMAIL = "r.pandey@shreegauripharma.in";

function fmtDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function fmtDuration(seconds = 0) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 60) return "< 1 min";
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

function Status({ value, current }) {
  const map = {
    online: ["bg-emerald-500", "Online now", "text-emerald-700 bg-emerald-50 border-emerald-200"],
    idle: ["bg-amber-400", "Idle", "text-amber-700 bg-amber-50 border-amber-200"],
    ended: ["bg-slate-300", "Ended", "text-slate-600 bg-slate-50 border-slate-200"],
  };
  const [dot, label, style] = map[value] || map.ended;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10px] font-bold ${style}`}>
      <span className={`h-2 w-2 rounded-full ${dot} ${value === "online" ? "animate-pulse" : ""}`} />
      {current ? "This device · " : ""}{label}
    </span>
  );
}

export default function LoginActivity() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState("");

  const allowed = user?.email?.toLowerCase() === OWNER_EMAIL;

  const load = useCallback(async (silent = false) => {
    if (!allowed) return;
    if (!silent) setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/login-activity");
      setData(payload);
    } catch (err) {
      toast.error(err.message || "Login activity load nahi hui");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 20000);
    return () => clearInterval(timer);
  }, [load]);

  const endSession = async (id) => {
    setEnding(id);
    try {
      await api.post(`/admin/sessions/${id}/revoke`);
      toast.success("Session signed out");
      await load(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnding("");
    }
  };

  const attempts = useMemo(() => (data?.attempts || []).slice(0, 40), [data]);

  if (!allowed) {
    return <div className="py-16 text-center text-sm text-slate-500">This private section is not available for this account.</div>;
  }

  if (loading && !data) {
    return <div className="py-16 text-center text-sm text-slate-500">Loading login activity...</div>;
  }

  return (
    <div className="sgp-page" data-testid="login-activity-page">
      <div className="sgp-page-header">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-teal-700" />
            <h1 className="sgp-page-title">Login Activity</h1>
          </div>
          <p className="sgp-page-subtitle">Private security view — only {OWNER_EMAIL} can open this section.</p>
        </div>
        <button onClick={() => load()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={Wifi} label="Online now" value={data?.online_count || 0} tone="emerald" />
        <Card icon={Activity} label="Open sessions" value={data?.open_session_count || 0} tone="blue" />
        <Card icon={Clock3} label="Logins today" value={data?.sessions_today || 0} tone="slate" />
        <Card icon={AlertTriangle} label="Failed attempts · 7d" value={data?.failed_attempts_7d || 0} tone="amber" />
      </div>

      <section className="sgp-table-shell overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-slate-900">Session Activity</h2>
            <p className="text-xs text-slate-500 mt-0.5">Login time, device, IP, last activity and total session duration.</p>
          </div>
          <span className="text-[10px] text-slate-400">Auto-refresh 20s</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-sm">
            <thead><tr>
              <th className="sgp-th">Status</th><th className="sgp-th">User</th><th className="sgp-th">Device</th>
              <th className="sgp-th">Login</th><th className="sgp-th">Last active</th><th className="sgp-th">Duration</th>
              <th className="sgp-th">IP</th><th className="sgp-th">Method</th><th className="sgp-th"></th>
            </tr></thead>
            <tbody>
              {(data?.sessions || []).map((s) => (
                <tr key={s.id} className="sgp-table-row">
                  <td className="sgp-td"><Status value={s.status} current={s.current} /></td>
                  <td className="sgp-td"><p className="font-semibold text-slate-800">{s.user_name || s.user_email || "Unknown"}</p><p className="text-[10px] font-mono text-slate-500">{s.user_email}</p></td>
                  <td className="sgp-td"><div className="flex items-center gap-2"><Laptop className="h-4 w-4 text-slate-400" /><div><p className="text-xs font-semibold">{s.device}</p><p className="text-[10px] text-slate-500">{s.browser} · {s.os}</p></div></div></td>
                  <td className="sgp-td text-xs whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="sgp-td text-xs whitespace-nowrap">{fmtDate(s.last_seen)}</td>
                  <td className="sgp-td font-mono text-xs font-semibold">{fmtDuration(s.duration_seconds)}</td>
                  <td className="sgp-td font-mono text-xs">{s.ip || "-"}</td>
                  <td className="sgp-td text-xs">{s.sign_in_method || "-"}</td>
                  <td className="sgp-td">
                    {s.status !== "ended" && !s.current && (
                      <button disabled={ending === s.id} onClick={() => endSession(s.id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 border border-rose-200 rounded px-2 py-1 hover:bg-rose-50 disabled:opacity-50">
                        <LogOut className="h-3.5 w-3.5" /> {ending === s.id ? "Ending..." : "End"}
                      </button>
                    )}
                    {s.current && <span className="text-[10px] text-slate-400">Current</span>}
                  </td>
                </tr>
              ))}
              {!data?.sessions?.length && <tr><td colSpan={9} className="sgp-td py-10 text-center text-slate-400">No sessions recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sgp-table-shell overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="font-heading font-bold text-slate-900">Recent Sign-in Attempts</h2>
          <p className="text-xs text-slate-500 mt-0.5">Successful, failed, denied and locked sign-in events.</p>
        </div>
        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="sticky top-0 bg-white"><tr>
              <th className="sgp-th">Time</th><th className="sgp-th">Email</th><th className="sgp-th">Result</th>
              <th className="sgp-th">Device</th><th className="sgp-th">IP</th><th className="sgp-th">Detail</th>
            </tr></thead>
            <tbody>
              {attempts.map((a) => (
                <tr key={a.id}>
                  <td className="sgp-td text-xs whitespace-nowrap">{fmtDate(a.at)}</td>
                  <td className="sgp-td font-mono text-xs">{a.email}</td>
                  <td className="sgp-td"><span className={`text-xs font-bold ${a.result === "success" ? "text-emerald-700" : a.result === "locked" ? "text-rose-700" : "text-amber-700"}`}>{a.result}</span></td>
                  <td className="sgp-td text-xs">{a.device} · {a.browser}</td>
                  <td className="sgp-td font-mono text-xs">{a.ip || "-"}</td>
                  <td className="sgp-td text-xs text-slate-500">{a.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Card({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.slate}`}>
      <Icon className="h-4 w-4 opacity-80" />
      <p className="text-2xl font-bold mt-3">{value}</p>
      <p className="text-xs font-semibold mt-0.5">{label}</p>
    </div>
  );
}
